<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import AssetPlayer from '@/players/embed/AssetPlayer.vue'
import { useSharedAssetPlayerStore } from '../../stores/useSharedAssetPlayerStore'

const sharedPlayer = useSharedAssetPlayerStore()

// Hidden fallback host: keeps the Teleport target valid (so the player is never
// torn down) during the brief window when a mini ↔ modal handoff is in flight.
const parkingEl = ref<HTMLElement | null>(null)
const playerRef = ref<InstanceType<typeof AssetPlayer> | null>(null)

onMounted(() => {
  sharedPlayer.setParkingHost(parkingEl.value)
  sharedPlayer.setControls({
    play: () => playerRef.value?.play(),
    pause: () => playerRef.value?.pause(),
  })
})

onBeforeUnmount(() => {
  sharedPlayer.setParkingHost(null)
  sharedPlayer.setControls(null)
})
</script>

<template>
  <div ref="parkingEl" class="shared-asset-player-parking" aria-hidden="true" />
  <Teleport v-if="sharedPlayer.asset && sharedPlayer.teleportTarget" :to="sharedPlayer.teleportTarget">
    <AssetPlayer
      ref="playerRef"
      :key="sharedPlayer.asset.uid"
      :asset-id="sharedPlayer.asset.uid"
      :asset-type="sharedPlayer.asset.type"
      :auto-play="true"
      :start-at="sharedPlayer.position"
      :show-logo="false"
      :enable-asset-link="sharedPlayer.mode === 'modal' && !sharedPlayer.asset.viaAssetId"
      :via-asset-id="sharedPlayer.asset.viaAssetId"
      @time-update="sharedPlayer.setPosition($event)"
      @play="sharedPlayer.setPlaying(true)"
      @pause="sharedPlayer.setPlaying(false)"
      @ended="sharedPlayer.setPlaying(false)"
    />
  </Teleport>
</template>

<style scoped lang="scss">
.shared-asset-player-parking {
  position: fixed;
  bottom: 0;
  right: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}
</style>
